/**
 * Pipeline topology scene — Production Line Campus.
 *
 * Campus layout:
 *   - Entry building (5x5 manager) on the left
 *   - Stage buildings (4x4 factory) in a horizontal line
 *   - Stone paths connecting each pair of adjacent buildings
 *   - Conveyor furniture along the paths
 *
 * Scales linearly: more stages = longer row of buildings.
 */

import type { SceneLayout, TopologyInfo, BuildingFootprint, CampusDef } from "./SceneLayout.js";
import { campusToLayout, routePath } from "./SceneLayout.js";
import { getFurnitureForBuilding } from "./FurniturePresets.js";

const ENTRY_SIZE = 6;
const STAGE_SIZE = 4;
const GAP = 4;

/** Walk edges from entry point to build pipeline order. */
function buildPipelineOrder(topo: TopologyInfo): string[] {
  const order: string[] = [];
  const visited = new Set<string>();
  let current = topo.entryPoint ?? topo.agents[0]?.id;

  while (current && !visited.has(current)) {
    visited.add(current);
    order.push(current);
    const edge = topo.edges.find((e) => e.from === current && !visited.has(e.to));
    current = edge?.to ?? "";
  }

  for (const a of topo.agents) {
    if (!visited.has(a.id)) {
      order.push(a.id);
    }
  }

  return order;
}

export function pipelineLayout(topo: TopologyInfo): SceneLayout {
  const order = buildPipelineOrder(topo);
  const buildings: BuildingFootprint[] = [];

  let currentCol = 0;

  for (let i = 0; i < order.length; i++) {
    const isEntry = i === 0;
    const size = isEntry ? ENTRY_SIZE : STAGE_SIZE;

    buildings.push({
      id: `stage-${i}`,
      col: currentCol,
      row: 0,
      width: size,
      height: size,
      floorType: isEntry ? "manager" : "factory",
      agents: [{
        agentId: order[i],
        localCol: Math.floor(size / 2),
        localRow: Math.floor(size / 2),
        deskType: isEntry ? "large" : "small",
        isEntryPoint: order[i] === topo.entryPoint,
      }],
      furniture: getFurnitureForBuilding(
        isEntry ? "entry" : "worker",
        "factory",
        currentCol, 0, size, size,
      ),
    });

    currentCol += size + GAP;
  }

  // Route paths between adjacent stages
  const pathCells: Array<{ col: number; row: number }> = [];
  for (let i = 0; i < buildings.length - 1; i++) {
    pathCells.push(...routePath(buildings[i], buildings[i + 1]));
  }

  // Add conveyor furniture along paths between buildings
  const decorations = buildings.slice(0, -1).map((b, i) => {
    const nextB = buildings[i + 1];
    const midCol = b.col + b.width + Math.floor((nextB.col - b.col - b.width) / 2);
    const midRow = Math.floor(b.height / 2);
    return { type: "conveyor" as const, gridCol: midCol, gridRow: midRow };
  });

  const campus: CampusDef = {
    buildings,
    pathCells,
    decorations,
  };

  return campusToLayout(campus);
}
